import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Zap,
  Plus,
  MessageSquare,
  Trash2,
  Edit2,
  Layout,
  ShieldCheck,
  Cpu,
  RefreshCw,
  X,
  AlertCircle,
  Info,
  Copy
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import alertService from '../../utils/alert';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import CopyRuleToPlatformModal from '../../components/automation/CopyRuleToPlatformModal';

const BLANK_FORM = {
  name: '',
  triggerType: 'keyword',
  keywords: '',
  matchingType: 'contains',
  actionType: 'send_text',
  actionConfig: {
    text: '',
    templateId: '',
    flowId: ''
  },
  priority: 0,
  isActive: true
};

const MetaAutomationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [flows, setFlows] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [copyRule, setCopyRule] = useState(null);

  const [formData, setFormData] = useState(BLANK_FORM);

  // A rule duplicated from another platform arrives here as router state —
  // open the New Rule form pre-filled with it, then clear the state so a
  // refresh or back-navigation doesn't re-open it.
  useEffect(() => {
    if (location.state?.prefillRule) {
      const { prefillRule } = location.state;
      setEditingRule(null);
      setFormData({
        ...BLANK_FORM,
        ...prefillRule,
        keywords: (prefillRule.keywords || []).join(', '),
        actionConfig: { ...BLANK_FORM.actionConfig, ...prefillRule.actionConfig },
      });
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/meta/automation');
      setRules(res.data || []);
    } catch (err) {
      alertService.error('Fetch Failed', 'Could not retrieve automation rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/meta/templates');
      setTemplates((res.data.templates || []).filter(t => t.status === 'APPROVED'));
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchFlows = useCallback(async () => {
    try {
      const res = await api.get('/whatsapp-flows');
      setFlows(res.data.flows || res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await api.get('/meta/logs', { params: { limit: 30 } });
      setLogs(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchTemplates();
    fetchFlows();
    fetchLogs();
  }, [fetchRules, fetchTemplates, fetchFlows, fetchLogs]);

  const handleSave = async (e) => {
    e.preventDefault();

    const keywordList = typeof formData.keywords === 'string'
      ? formData.keywords.split(',').map(k => k.trim()).filter(Boolean)
      : formData.keywords;

    // The keyword field is always comma-split into independent patterns
    // (the matcher OR's each one — see MetaAutomationService._isMatch), but
    // that's silently wrong for a legitimate single regex containing a
    // comma, e.g. \d{2,4} or (foo|bar){1,2} — the quantifier's comma gets
    // cut, producing two invalid fragments that never match anything and
    // fail completely silently (the backend only logs it server-side; nothing
    // ever showed up in this page's own Activity Log). Catch it here instead
    // of letting the rule save and quietly never fire.
    if (formData.matchingType === 'regex') {
      const invalid = keywordList.filter((pattern) => {
        try { new RegExp(pattern, 'i'); return false; } catch { return true; }
      });
      if (invalid.length) {
        alertService.error(
          'Invalid pattern',
          `"${invalid[0]}" isn't a valid regular expression. If your pattern itself contains a comma (e.g. \\d{2,4}), enter it as the only pattern — commas here separate multiple alternative patterns.`
        );
        return;
      }
    }

    setLoading(true);
    try {
      const payload = { ...formData, keywords: keywordList };

      if (editingRule) {
        await api.put(`/meta/automation/${editingRule._id}`, payload);
        alertService.toast('success', 'Automation rule updated.');
      } else {
        await api.post('/meta/automation', payload);
        alertService.toast('success', 'Automation rule created.');
      }
      setShowForm(false);
      fetchRules();
    } catch (err) {
      alertService.error('Save Failed', err.response?.data?.error || 'Could not save automation rule.');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (rule) => {
    try {
      await api.put(`/meta/automation/${rule._id}`, { isActive: !rule.isActive });
      fetchRules();
      alertService.toast('info', `Automation ${rule.isActive ? 'paused' : 'resumed'}.`);
    } catch (err) {
      alertService.error('Status Error', 'Failed to toggle rule state.');
    }
  };

  const deleteRule = async (id) => {
    const confirmed = await alertService.confirm('Delete Rule', 'Are you sure you want to delete this automation rule?');
    if (!confirmed) return;

    try {
      await api.delete(`/meta/automation/${id}`);
      alertService.toast('success', 'Rule deleted successfully.');
      fetchRules();
    } catch (err) {
      alertService.error('Delete Failed', 'Could not remove the specified rule.');
    }
  };

  const stats = [
    { label: 'Active Rules', value: rules.filter(r => r.isActive).length, icon: Zap, color: 'text-[#04844B]' },
    { label: 'Total Rules', value: rules.length, icon: Cpu, color: 'text-[#0176D3]' },
    { label: 'Auto Response', value: 'Instant', icon: RefreshCw, color: 'text-[#0176D3]' },
  ];

  return (
    <DashboardModuleShell
      title="Meta Automation"
      subtitle="Set up auto-reply rules and keyword-driven responses for your official Meta channels."
      icon={Zap}
      stats={stats}
      actions={
        <div className="flex items-center gap-3">
          <Button variant="outline" icon={RefreshCw} onClick={fetchRules} loading={loading}>Refresh</Button>
          <Button onClick={() => { setEditingRule(null); setFormData(BLANK_FORM); setShowForm(true); }} icon={Plus}>New Rule</Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Rules List */}
        <div className="sf-card">
          <div className="sf-card-header flex items-center justify-between">
            <h3 className="sf-card-title">Automation Rules</h3>
            <span className="sf-badge sf-badge-manager">{rules.length} Rules</span>
          </div>

          <div className="sf-table-container border-0 shadow-none">
            <div className="overflow-x-auto">
              <table className="sf-table">
                <thead>
                  <tr>
                    <th>Rule Name</th>
                    <th>Keywords</th>
                    <th>Action</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && rules.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-16 text-center">
                        <RefreshCw className="w-8 h-8 animate-spin text-[#0176D3] mx-auto mb-3" />
                        <p className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wider">Loading rules…</p>
                      </td>
                    </tr>
                  ) : rules.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-16 text-center px-6">
                        <div className="w-16 h-16 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center mb-4 mx-auto">
                          <Zap className="w-8 h-8 text-[#706E6B]" />
                        </div>
                        <h3 className="text-[14px] font-bold text-[#080707] mb-1">No Automation Rules Yet</h3>
                        <p className="text-[13px] text-[#706E6B] mb-6 max-w-sm mx-auto">Create your first keyword-based auto-reply rule to start automating your official WhatsApp presence.</p>
                        <Button onClick={() => setShowForm(true)} icon={Plus} variant="outline">Create First Rule</Button>
                      </td>
                    </tr>
                  ) : (
                    rules.map(rule => (
                      <tr key={rule._id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-8 h-8 rounded-md flex items-center justify-center shrink-0",
                              rule.isActive ? "bg-[#EAF4FE] text-[#0176D3]" : "bg-[#F3F2F2] text-[#706E6B]"
                            )}>
                              <Cpu size={16} />
                            </div>
                            <div>
                              <p className="text-[13px] font-bold text-[#080707]">{rule.name}</p>
                              <p className="text-[11px] text-[#706E6B]">Priority: {rule.priority}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1.5 items-center">
                            {rule.keywords.map((k, i) => (
                              <span key={i} className="sf-badge sf-badge-manager">
                                {k}
                              </span>
                            ))}
                            <span className="text-[11px] text-[#706E6B]">({rule.matchingType})</span>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-[#F4F6F9] rounded border border-[#D8DDE6] flex items-center justify-center text-[#706E6B]">
                              {rule.actionType === 'send_text' ? <MessageSquare size={14} /> : <Layout size={14} />}
                            </div>
                            <span className="text-[13px] font-medium text-[#080707]">
                              {rule.actionType.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => toggleStatus(rule)}
                            className={cn(
                              "sf-badge",
                              rule.isActive ? "sf-badge-success" : "sf-badge-error"
                            )}
                          >
                            {rule.isActive ? 'Active' : 'Paused'}
                          </button>
                        </td>
                        <td className="text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => setCopyRule(rule)} title="Duplicate to…" aria-label="Duplicate rule to another platform" className="p-1.5 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors">
                              <Copy size={14} />
                            </button>
                            <button onClick={() => { setEditingRule(rule); setFormData({ ...rule, keywords: rule.keywords.join(', ') }); setShowForm(true); }} title="Edit" aria-label="Edit rule" className="p-1.5 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors">
                              <Edit2 size={14} />
                            </button>
                            <button onClick={() => deleteRule(rule._id)} title="Delete" aria-label="Delete rule" className="p-1.5 text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FDE7E9] rounded transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="sf-card p-4 flex items-start gap-3">
          <ShieldCheck className="text-[#0176D3] mt-0.5 shrink-0" size={18} />
          <div>
            <h4 className="text-[13px] font-bold text-[#080707]">How Automation Rules Work</h4>
            <p className="text-[12px] text-[#706E6B] leading-relaxed mb-0">
              Automation rules match incoming messages against keywords and send an automated reply. When multiple rules match, the rule with the higher priority value is applied first.
            </p>
          </div>
        </div>

        {/* Activity Log — surfaces exactly why a matched rule's send failed
            (bad token, unpublished flow, plan cap, etc.) instead of leaving
            "it matched but nothing arrived" as an unexplained dead end. */}
        <div className="sf-card">
          <div className="sf-card-header flex items-center justify-between">
            <h3 className="sf-card-title">Activity Log</h3>
            <Button variant="outline" icon={RefreshCw} onClick={fetchLogs} loading={logsLoading}>Refresh</Button>
          </div>
          <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
            {logs.length === 0 && !logsLoading && (
              <p className="text-[12px] text-[#706E6B]">No activity recorded yet. Rule matches and send attempts (including failures) will show up here.</p>
            )}
            {logs.map((log) => (
              <div key={log._id} className={cn(
                'flex items-start gap-2 p-2.5 rounded-md border text-[12px]',
                log.level === 'error' ? 'bg-[#FDE7E9] border-[#F5B4B8]' : 'bg-[#F4F6F9] border-[#D8DDE6]'
              )}>
                {log.level === 'error' ? (
                  <AlertCircle size={14} className="text-[#BA0517] mt-0.5 shrink-0" />
                ) : (
                  <Info size={14} className="text-[#706E6B] mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#080707]">{log.message}</span>
                    <span className="text-[10px] uppercase tracking-wide text-[#706E6B] shrink-0">{log.category}</span>
                  </div>
                  <span className="text-[11px] text-[#706E6B]">{new Date(log.timestamp).toLocaleString()}</span>
                  {log.details?.error && (
                    <p className="text-[11px] text-[#BA0517] mt-1 break-words">{typeof log.details.error === 'string' ? log.details.error : JSON.stringify(log.details.error)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rule Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="bg-white w-full max-w-2xl rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col overflow-y-auto max-h-[100dvh]">
            <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-b border-[#E5E5E5] shrink-0">
              <h3 className="text-[14px] font-bold text-[#080707]">
                {editingRule ? 'Edit Automation Rule' : 'New Automation Rule'}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center hover:bg-white rounded transition-all border border-transparent hover:border-[#D8DDE6] text-[#706E6B]">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSave} className="flex-1 p-6 overflow-y-auto space-y-6">
              <div className="space-y-2">
                <label className="text-[12px] font-bold text-[#706E6B]">Rule Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Inbound Pricing Inquiry"
                  className="w-full h-10 px-3 bg-white border border-[#D8DDE6] rounded-md text-[13px] outline-none focus:border-[#0176D3] transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[12px] font-bold text-[#706E6B] block">Matching Type</label>
                  <select
                    className="w-full h-10 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none cursor-pointer focus:border-[#0176D3] transition-all"
                    value={formData.matchingType}
                    onChange={(e) => setFormData({ ...formData, matchingType: e.target.value })}
                  >
                    <option value="contains">Contains Keyword</option>
                    <option value="exact">Exact Match</option>
                    <option value="regex">Pattern (Regex)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[12px] font-bold text-[#706E6B] block">Priority</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    className="w-full h-10 px-3 bg-white border border-[#D8DDE6] rounded-md text-[13px] outline-none focus:border-[#0176D3] transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[12px] font-bold text-[#706E6B] block">
                  {formData.matchingType === 'regex' ? 'Pattern(s) — comma separates alternatives' : 'Keywords (comma separated)'}
                </label>
                <input
                  type="text"
                  required
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  placeholder={formData.matchingType === 'regex' ? '^order.*status$, ^track.*order$' : 'price, cost, how much, quoting'}
                  className="w-full h-10 px-3 bg-white border border-[#D8DDE6] rounded-md text-[13px] outline-none focus:border-[#0176D3] transition-all"
                />
                {formData.matchingType === 'regex' && (
                  <p className="text-[11px] text-[#706E6B]">
                    Each comma-separated entry is its own independent pattern — any one matching fires the rule. If a single pattern needs a literal comma (e.g. a quantifier like <code>\d{'{2,4}'}</code>), enter only that one pattern with no others.
                  </p>
                )}
              </div>

              <div className="space-y-3 pt-4 border-t border-[#D8DDE6]">
                <label className="text-[12px] font-bold text-[#706E6B] block">Action</label>
                <div className="flex gap-3">
                  {[
                    { id: 'send_text', label: 'Plain Text', icon: MessageSquare },
                    { id: 'send_template', label: 'Template', icon: Layout },
                    { id: 'send_flow', label: 'WhatsApp Flow', icon: Zap }
                  ].map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, actionType: type.id })}
                      className={cn(
                        "flex-1 p-3 rounded-md border text-[12px] font-bold transition-all flex flex-col items-center gap-2",
                        formData.actionType === type.id ? "bg-[#EAF4FE] border-[#0176D3] text-[#0176D3]" : "bg-white border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3]"
                      )}
                    >
                      <type.icon size={18} />
                      {type.label}
                    </button>
                  ))}
                </div>

                {formData.actionType === 'send_text' && (
                  <textarea
                    rows={5}
                    required
                    value={formData.actionConfig.text}
                    onChange={(e) => setFormData({ ...formData, actionConfig: { ...formData.actionConfig, text: e.target.value } })}
                    placeholder="Enter the automated response that will be sent to customers…"
                    className="w-full px-3 py-3 bg-white border border-[#D8DDE6] rounded-md text-[13px] leading-relaxed outline-none focus:border-[#0176D3] transition-all resize-none"
                  />
                )}

                {formData.actionType === 'send_template' && (
                  <select
                    required
                    className="w-full h-10 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none cursor-pointer focus:border-[#0176D3] transition-all"
                    value={formData.actionConfig.templateId}
                    onChange={(e) => setFormData({ ...formData, actionConfig: { ...formData.actionConfig, templateId: e.target.value } })}
                  >
                    <option value="">-- Select Approved Meta Template --</option>
                    {templates.map(t => <option key={t._id} value={t._id}>{t.name} ({t.language})</option>)}
                  </select>
                )}

                {formData.actionType === 'send_flow' && (
                  <select
                    required
                    className="w-full h-10 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none cursor-pointer focus:border-[#0176D3] transition-all"
                    value={formData.actionConfig.flowId}
                    onChange={(e) => setFormData({ ...formData, actionConfig: { ...formData.actionConfig, flowId: e.target.value } })}
                  >
                    <option value="">-- Select WhatsApp Flow --</option>
                    {flows.map(f => <option key={f._id} value={f._id}>{f.name} ({f.metaFlowId})</option>)}
                  </select>
                )}
              </div>

              <div className="pt-4 border-t border-[#D8DDE6] flex justify-end gap-3">
                <Button variant="outline" type="button" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" loading={loading}>Save</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <CopyRuleToPlatformModal
        isOpen={!!copyRule}
        onClose={() => setCopyRule(null)}
        sourcePlatform="whatsapp"
        rule={copyRule}
      />
    </DashboardModuleShell>
  );
};

export default MetaAutomationPage;
